import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { logout, getAllOrganizers, getAllEventsGlobal, getAllUsersGlobal } from '../../services/api';
import { useQuery } from '@tanstack/react-query';

/**
 * MasterSidebar - Menu lateral para el panel super-admin
 */
const MasterSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();

    const { data: organizers = [] } = useQuery({ queryKey: ['admin-all-organizers'], queryFn: getAllOrganizers });
    const { data: events = [] } = useQuery({ queryKey: ['admin-all-events'], queryFn: getAllEventsGlobal });
    const { data: users = [] } = useQuery({ queryKey: ['admin-all-users'], queryFn: getAllUsersGlobal });

    const menuItems = [
        { label: 'Visión Global', icon: '🌍', path: '/master-admin' },
        { label: 'Organizadores', icon: '🏢', path: '/master-admin/organizers', count: organizers.length, color: 'bg-blue-500' },
        { icon: '🏟️', label: 'Gestión de Clubes', path: '/master-admin/clubs' },
        { icon: '💳', label: 'Suscripciones', path: '/master-admin/subscriptions' },
        { label: 'Todos los Eventos', icon: '🎾', path: '/master-admin/events', count: events.length, color: 'bg-primary-500' },
        { label: 'Base de Usuarios', icon: '👥', path: '/master-admin/users', count: users.filter(u => u.role === 'player').length, color: 'bg-green-500' },
        { label: 'Configuración SaaS', icon: '🛠️', path: '/master-admin/settings' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-950/80 z-40 md:hidden backdrop-blur-md"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed md:sticky top-0 bottom-0 left-0 z-50
                w-64 bg-slate-950 text-white
                min-h-screen flex flex-col
                transition-transform duration-300 ease-in-out border-r border-slate-800
                ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                {/* Header del Sidebar */}
                <div className="p-8 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-3xl">🛡️</span>
                            <span className="text-xl font-black tracking-tighter text-white">MASTER</span>
                        </div>
                        <p className="text-[8px] text-primary-400 font-black uppercase tracking-[0.2em] italic">Administración SaaS</p>
                    </div>
                    {/* Mobile Close Button */}
                    <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
                        ✕
                    </button>
                </div>

                {/* Menu Nav */}
                <nav className="flex-grow p-4 mt-6 space-y-2 overflow-y-auto">
                    {menuItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === '/master-admin'}
                            onClick={() => onClose && onClose()}
                            className={({ isActive }) => `
                                flex items-center gap-3 px-4 py-3 rounded-2xl transition-all font-bold text-sm uppercase tracking-wider
                                ${isActive
                                    ? 'bg-primary-600 text-slate-950 shadow-lg shadow-primary-500/20'
                                    : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'}
                            `}
                        >
                            <span className="text-xl">{item.icon}</span>
                            <span className="flex-grow">{item.label}</span>
                            {item.count !== undefined && (
                                <span className={`text-[10px] font-black text-white px-2 py-0.5 rounded-full ${item.color || 'bg-slate-700'}`}>
                                    {item.count}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Footer del Sidebar */}
                <div className="p-6 border-t border-slate-800">
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all font-black text-xs uppercase tracking-widest text-red-400 hover:bg-red-500/10 hover:text-red-300 w-full border border-red-500/20"
                    >
                        <span>🚪</span>
                        <span>Salir del Sistema</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default MasterSidebar;
